import React, { useCallback, useMemo, useState } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  endOfMonth,
  endOfQuarter,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfQuarter,
} from "date-fns/esm";
import { useIntl } from "react-intl";

import { Deal } from "@mapmycustomers/shared/types/entity";
import { TextField } from "@mapmycustomers/ui";

import DatePicker from "@app/component/input/DatePicker";
import EntityModal from "@app/component/modal/EntityModal";
import { formatDate } from "@app/util/formatters";
import { parseApiDate, parseApiDateWithUtcTz } from "@app/util/parsers";

import ForecastViewMode from "../enum/ForecastViewMode";

import useNameDateFormat from "./ForecastBoard/util/useNameDateFormat";
import styles from "./SelectClosingDate.module.scss";

interface Props {
  deal: Deal;
  forecastViewMode: ForecastViewMode;
  onHide: () => void;
  onSelect: (dateString: string | undefined) => void;
  targetDate?: Date;
}

const SelectClosingDateModal: React.FC<Props> = ({
  deal,
  forecastViewMode,
  onHide,
  onSelect,
  targetDate,
}) => {
  const intl = useIntl();
  const isPastClosingDate =
    targetDate &&
    (forecastViewMode === ForecastViewMode.MONTH
      ? isBefore(targetDate, startOfMonth(Date.now()))
      : isBefore(targetDate, startOfQuarter(Date.now())));
  const nameDateFormat = useNameDateFormat(forecastViewMode, intl);
  const [expectedDay, setExpectedDay] = useState<Date | null>(
    targetDate
      ? forecastViewMode === ForecastViewMode.MONTH
        ? endOfMonth(targetDate)
        : endOfQuarter(targetDate)
      : null
  );

  const minDate = useMemo(() => {
    if (isPastClosingDate) {
      return;
    }
    return startOfDay(
      forecastViewMode === ForecastViewMode.MONTH
        ? startOfMonth(targetDate ?? new Date())
        : startOfQuarter(targetDate ?? new Date())
    );
  }, [isPastClosingDate, targetDate, forecastViewMode]);

  const maxDate = useMemo(
    () =>
      startOfDay(
        forecastViewMode === ForecastViewMode.MONTH
          ? endOfMonth(targetDate ?? new Date())
          : endOfQuarter(targetDate ?? new Date())
      ),
    [targetDate, forecastViewMode]
  );

  const isDateDisabled = useCallback(
    (date: Date) => (minDate && isBefore(date, minDate)) || isAfter(date, maxDate),
    [minDate, maxDate]
  );

  const handleConfirm = useCallback(() => {
    onSelect(
      expectedDay ? parseApiDate(formatDate(expectedDay, "yyyy-MM-dd")).toISOString() : undefined
    );
  }, [expectedDay, onSelect]);

  return (
    <EntityModal
      cancelText={intl.formatMessage({
        id: "deals.setClosingDateModal.cancel",
        defaultMessage: "Cancel",
        description: "Cancel button on Change Close Date",
      })}
      okText={intl.formatMessage({
        id: "deals.setClosingDateModal.ok",
        defaultMessage: "Save And Update Close Date",
        description: "Ok button on Change Close Date",
      })}
      onCancel={onHide}
      onOk={handleConfirm}
      title={
        targetDate
          ? intl.formatMessage({
              id: "deals.setClosingDateModal.title",
              defaultMessage: "Change Close Date",
              description: "Title of Change Close Date modal",
            })
          : intl.formatMessage({
              id: "deals.setClosingDateModal.noDate.title",
              defaultMessage: "Remove closing date?",
              description: "Title of Delete Close Date modal",
            })
      }
      visible
    >
      <div className={styles.container}>
        <div>
          {targetDate
            ? isPastClosingDate
              ? intl.formatMessage(
                  {
                    id: "deals.setPastClosingDateModal.olderClosingDate.description",
                    defaultMessage:
                      "To move this deal to <b>“Older”</b>, you’ll need to specify a new date within the appropriate time range.",
                    description: "Description of Change Close Date modal",
                  },
                  {
                    b: (text) => <b>{text}</b>,
                  }
                )
              : intl.formatMessage(
                  {
                    id: "deals.setClosingDateModal.description",
                    defaultMessage:
                      "To move this deal to “{date}”, you’ll need to specify a new date within the appropriate time range.",
                    description: "Description of Change Close Date modal",
                  },
                  {
                    date: (
                      <b>
                        {formatDate(
                          targetDate,
                          forecastViewMode === ForecastViewMode.MONTH ? "MMMM yyyy" : "qqq yyyy"
                        )}
                      </b>
                    ),
                  }
                )
            : intl.formatMessage({
                id: "deals.setClosingDateModal.noDate.description",
                defaultMessage:
                  "To move this deal to this column, you’ll be removing the close date. Are you sure you want to move this deal?",
                description: "Description of Delete Close Date modal",
              })}
        </div>

        <div className={styles.closeDateGroup}>
          <div>
            <div className={styles.label}>
              {targetDate
                ? intl.formatMessage({
                    id: "deals.setClosingDateModal.previousDate",
                    defaultMessage: "Previous close date",
                    description: "Previous of Change Close Date modal",
                  })
                : intl.formatMessage({
                    id: "deals.setClosingDateModal.currentDate",
                    defaultMessage: "Current close date",
                    description: "Current date of delete Close Date modal",
                  })}
            </div>

            {deal.closingDate ? (
              <DatePicker
                className={styles.input}
                disabled
                format="PPP"
                suffixIcon={<FontAwesomeIcon icon={faLock} />}
                value={parseApiDateWithUtcTz(deal.closingDate)}
              />
            ) : (
              <TextField
                className={styles.input}
                defaultValue={intl.formatMessage({
                  id: "deals.setClosingDateModal.currentDate.noDate",
                  defaultMessage: "No date",
                  description: "Current date of delete Close Date modal when it's not defined",
                })}
                disabled
                suffix={<FontAwesomeIcon icon={faLock} />}
              />
            )}
          </div>

          {targetDate && (
            <div>
              <div className={styles.periodGroup}>
                <div className={styles.label}>
                  {intl.formatMessage({
                    id: "deals.setClosingDateModal.nextDate.description",
                    defaultMessage: "New close date",
                    description: "Previous of Change Close Date modal",
                  })}
                </div>

                <div className={styles.period}>
                  {isPastClosingDate
                    ? intl.formatMessage(
                        {
                          id: "deals.setClosingDateModal.pastClosingDate.description",
                          defaultMessage: "Before {date}",
                          description: "Description of New Expected Close Date",
                        },
                        {
                          date: <b>{formatDate(new Date(), nameDateFormat)}</b>,
                        }
                      )
                    : `${formatDate(minDate!, "PP")} - ${formatDate(maxDate, "PP")}`}
                </div>
              </div>

              <DatePicker
                className={styles.input}
                disabledDate={isDateDisabled}
                format="PPP"
                onChange={setExpectedDay}
                picker="date"
                value={expectedDay}
              />
            </div>
          )}
        </div>
      </div>
    </EntityModal>
  );
};

export default SelectClosingDateModal;
